import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getLeaderboard, type LeaderboardRow, type Sex } from "../lib/api";
import { useHub } from "../hooks/useHub";
import TabBar from "../components/TabBar";
import RefreshButton from "../components/RefreshButton";
import { useT } from "../i18n";

// Тот же лидерборд, что крутится на ТВ у станции гребли, — в мобильной вёрстке.
const POLL_MS = 5000;
const TOP_N = 10;

const RANK_TONES = [
  "text-gold-light", // 1
  "text-white/80", // 2
  "text-[#D9915B]", // 3
];

function LbList({
  rows,
  meNickname,
  t,
  emptyText,
}: {
  rows: LeaderboardRow[];
  meNickname: string | null;
  t: (k: string, v?: Record<string, string | number>) => string;
  emptyText: string;
}) {
  if (rows.length === 0) return <p className="text-muted text-sm px-1 py-2">{emptyText}</p>;
  // Своя строка подсвечивается по нику (телефона в публичном лидерборде нет).
  let meMarked = false;
  return (
    <div className="flex flex-col gap-1.5">
      {rows.map((r, i) => {
        const isMe = !meMarked && !!meNickname && r.nickname === meNickname;
        if (isMe) meMarked = true;
        return (
          <div
            key={`${r.nickname}-${i}`}
            className={`flex items-center gap-3 rounded-[14px] px-3.5 py-2.5 ${
              i === 0 ? "bg-gold/[0.10] border border-gold/40" : "bg-card border border-line-2"
            } ${isMe ? "ring-2 ring-violet" : ""}`}
          >
            <span className={`w-6 text-center font-display text-[17px] font-bold shrink-0 ${RANK_TONES[i] ?? "text-muted2"}`}>
              {i + 1}
            </span>
            <span className="flex-1 min-w-0 truncate text-[14px] font-semibold text-white">
              {r.nickname || "—"}
            </span>
            <span className="text-right shrink-0">
              <span className="block font-display text-[17px] font-bold text-white leading-none">
                {Math.round(r.power_w)} <span className="text-[11px] text-muted font-sans">{t("unit.watts")}</span>
              </span>
              <span className="block text-[11px] text-muted mt-0.5">
                {Math.round(r.distance_m)} {t("unit.meters")}
              </span>
            </span>
          </div>
        );
      })}
    </div>
  );
}

export default function Rowing() {
  const nav = useNavigate();
  const { t } = useT();
  const [params] = useSearchParams();
  const phone = params.get("phone") || "";
  const q = `?phone=${encodeURIComponent(phone)}`;

  const { hub } = useHub(phone || null);
  const [rows, setRows] = useState<LeaderboardRow[] | null>(null);
  // Вкладка зачёта: по умолчанию — свой пол (как только хаб его сообщит),
  // ручной выбор пользователя всегда важнее.
  const [sexTab, setSexTab] = useState<Sex | null>(null);
  const activeSex: Sex = sexTab ?? hub?.participant.sex ?? "F";

  // Лидерборд обновляется сам, как на ТВ у станции; кнопкой — мгновенно.
  const load = useCallback(
    () =>
      getLeaderboard()
        .then((r) => setRows(r.leaderboard))
        .catch(() => {
          /* переживём пропущенный тик — следующий обновит */
        }),
    [],
  );
  useEffect(() => {
    load();
    const timer = setInterval(load, POLL_MS);
    return () => clearInterval(timer);
  }, [load]);

  if (!phone) {
    return (
      <div className="min-h-dvh flex items-center justify-center px-6 text-muted text-sm">
        {t("hub.noPhone")}
      </div>
    );
  }

  const lbRows = (rows ?? [])
    .filter((r) => r.sex === activeSex)
    .sort((a, b) => b.power_w - a.power_w)
    .slice(0, TOP_N);

  const rowing = hub?.rowing ?? null;
  const meNickname = hub?.participant.nickname ?? null;

  return (
    <div className="min-h-dvh max-w-md mx-auto px-[22px] pt-4 pb-28">
      <header className="flex items-center gap-3 pt-1.5 mb-4">
        <button
          type="button"
          onClick={() => nav(`/hub${q}`)}
          aria-label={t("prizes.back")}
          className="w-9 h-9 rounded-full bg-white/[0.06] flex items-center justify-center active:bg-white/10 transition-colors shrink-0"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-[21px] font-extrabold tracking-tight leading-[1.1]">{t("stations.rowing.title")}</h1>
          <p className="text-[13px] text-muted mt-[3px]">{t("prizes.rowing.how")}</p>
        </div>
        <span className="flex items-center gap-1.5 bg-success/[0.12] border border-success/25 px-2.5 py-1.5 rounded-full shrink-0">
          <span
            className="w-[7px] h-[7px] rounded-full bg-success"
            style={{ boxShadow: "0 0 8px #3FCF6A", animation: "liveDot 1.8s ease-in-out infinite" }}
          />
          <span className="text-xs font-bold text-success">{t("hub.live")}</span>
        </span>
      </header>

      <div className="flex flex-col gap-3.5">
        {/* Мой результат */}
        <section className="relative bg-card-2-grad border border-line-3 rounded-[22px] p-5 overflow-hidden">
          <span
            className="absolute -right-10 -top-10 w-[140px] h-[140px] rounded-full"
            style={{ background: "radial-gradient(circle, rgba(124,92,252,0.3), transparent 70%)" }}
          />
          <h2 className="relative text-[13px] font-bold text-violet-lighter uppercase tracking-wide">
            {t("rowing.myResult")}
          </h2>
          {rowing ? (
            <div className="relative flex items-end gap-1.5 mt-2">
              <span className="font-display text-[44px] font-bold text-white leading-[0.9]">
                {Math.round(rowing.power_w)}
              </span>
              <span className="text-[16px] text-muted font-semibold mb-[6px]">{t("unit.watts")}</span>
              <span className="text-[14px] text-muted mb-[7px] ml-2">
                · {Math.round(rowing.distance_m)} {t("unit.meters")}
              </span>
            </div>
          ) : (
            <p className="relative text-sm text-muted leading-relaxed mt-2">{t("hub.rowing.empty")}</p>
          )}
        </section>

        {/* Мотивация: главный приз зоны */}
        <div className="rounded-[16px] border border-gold/40 bg-gold/[0.08] px-4 py-3.5 text-[13px] leading-[1.5] text-gold-light font-semibold">
          🏆 {t("rowing.motivation")}
        </div>

        {/* Окно соревнований и анонс итогов */}
        <p className="text-[12px] text-muted leading-[1.5] px-1">📅 {t("prizes.schedule.body")}</p>

        {/* Лидерборд с переключателем зачёта Ж / М */}
        <section>
          <div className="flex items-center justify-between mb-2.5">
            <div className="flex items-center gap-2">
              <h2 className="text-[15px] font-bold text-white">{t("rowing.lbTitle")}</h2>
              <RefreshButton onRefresh={load} label={t("admin.refresh")} />
            </div>
            <div className="flex gap-1.5">
              {(["F", "M"] as const).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setSexTab(s)}
                  className={`h-9 px-3.5 rounded-full text-[13px] font-bold transition-colors ${
                    activeSex === s
                      ? "bg-violet-grad text-white shadow-violet"
                      : "bg-white/[0.06] text-white/70 active:bg-white/10"
                  }`}
                >
                  {s === "F" ? t("prizes.rowing.women") : t("prizes.rowing.men")}
                </button>
              ))}
            </div>
          </div>
          <LbList rows={lbRows} meNickname={meNickname} t={t} emptyText={t("rowing.noRows")} />
        </section>
      </div>

      <TabBar active="home" phone={phone} />
    </div>
  );
}
